import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import WebIcon from './WebIcon';
import styles from './ItineraryEditor.styles';
import { coalesceText, mergePayload, normalizeExternalLink, resolveMapRouteModeMeta } from './itineraryUtils';

interface Props {
  it: any;
  onImageClick?: (images: any[], startIndex: number) => void;
}

const NoteAdapter: React.FC<Props> = ({ it, onImageClick }) => {
  console.log('[NoteAdapter] onImageClick:', typeof onImageClick, onImageClick);
  const merged = mergePayload(it) || {};
  const noteText = coalesceText(it?.note, merged.note, merged.text, it?.title);
  const mapLinkRaw =
    typeof merged.mapLink === 'string' ? merged.mapLink :
    typeof merged.navigationLink === 'string' ? merged.navigationLink :
    typeof merged.navigationUrl === 'string' ? merged.navigationUrl :
    undefined;
  const normalizedLink = normalizeExternalLink(mapLinkRaw);
  const mapModeRaw = coalesceText(merged.mapLinkMode, merged.routeMode, merged.transportMode, merged.mode);
  const mapModeMeta = resolveMapRouteModeMeta(mapModeRaw);
  const mapDuration = coalesceText(merged.mapLinkDuration, merged.routeDuration, merged.durationText, merged.duration);

  // 图片展示（朋友圈风格）
  console.log('🖼️ NoteAdapter 接收到的数据:', {
    hasCard: !!it?.card,
    cardId: it?.card?.id || it?.card_id,
    imageGallery: it?.card?.image_gallery,
    itKeys: Object.keys(it || {}),
  });
  // Debug: detect unexpected leading punctuation (e.g. full-width comma "，")
  try {
    if (typeof noteText === 'string' && noteText.length > 0 && noteText.charAt(0) === '，') {
      console.warn('[NoteAdapter] Detected leading punctuation in noteText', {
        noteText: noteText,
        json: JSON.stringify(noteText),
        codePoints: Array.from(noteText.slice(0, Math.min(8, noteText.length))).map(c => c.charCodeAt(0)),
      });
    }
  } catch (e) {
    // ignore debug failures
  }
  const imageGallery = it?.card?.image_gallery || [];
  const galleryImages = Array.isArray(imageGallery) ? imageGallery.filter((img: any) => img?.url) : [];

  // If this note actually contains an external link/payload, prefer showing
  // the external payload's explicit image (base_payload / merged payload)
  // instead of the user-uploaded gallery images. This prevents notes' images
  // from overriding external site previews in the timeline.
  const isExternalLike = Boolean(
    (merged && (merged.type === 'external' || merged.card_type === 'external')) ||
    merged.website || merged.url || merged.resolvedUrl || merged.resolved_url
  );

  const pickDirectString = (...vals: any[]) => {
    for (const v of vals) {
      if (typeof v === 'string') {
        const t = v.trim();
        if (t) return t;
      }
    }
    return undefined;
  };

  const basePayloadImage = pickDirectString(
    it?.card && it.card.base_payload && typeof it.card.base_payload.imageUrl === 'string' && it.card.base_payload.imageUrl,
    it?.card && it.card.base_payload && typeof it.card.base_payload.image_url === 'string' && it.card.base_payload.image_url,
    merged && typeof merged.imageUrl === 'string' && merged.imageUrl,
    merged && typeof merged.image_url === 'string' && merged.image_url,
    merged && typeof merged.image === 'string' && merged.image,
  );

  // Debug log to help trace why external preview might be ignored in timeline
  try {
    if (typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.host)) {
      console.log('@@TIMELINE-EXTERNAL-FINAL_IMAGE@@', { id: it?.id || it?.card?.id, isExternalLike, basePayloadImage, galleryFirst: galleryImages[0] && galleryImages[0].url });
    }
  } catch (e) {}

  const handleOpenLink = () => {
    if (!normalizedLink) return;
    try {
      if (typeof window !== 'undefined') {
        window.open(normalizedLink, '_blank', 'noopener,noreferrer');
        return;
      }
    } catch (e) {
      // fall through to location replace
    }
    if (typeof window !== 'undefined') {
      window.location.href = normalizedLink;
    }
  };

  // 渲染图片网格（类似朋友圈）
  const renderImageGallery = () => {
    if (galleryImages.length === 0) return null;

    return (
      <div style={{
        marginTop: 8,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 4,
        maxWidth: 280,
      }}>
        {galleryImages.slice(0, 9).map((img: any, idx: number) => (
          <div
            key={img.key || idx}
            onClick={(e) => {
              console.log('[NoteAdapter] Image clicked!', { idx, hasOnImageClick: !!onImageClick, galleryLength: galleryImages.length });
              e.preventDefault();
              e.stopPropagation();
              // 使用灯箱打开图片
              if (onImageClick) {
                console.log('[NoteAdapter] Calling onImageClick with', galleryImages.length, 'images');
                onImageClick(galleryImages, idx);
              } else {
                console.log('[NoteAdapter] No onImageClick callback, opening in new tab');
                if (typeof window !== 'undefined') {
                  window.open(img.url, '_blank');
                }
              }
            }}
            style={{
              position: 'relative',
              paddingBottom: '100%',
              borderRadius: 6,
              overflow: 'hidden',
              background: '#F3F4F6',
              cursor: 'pointer',
            }}
          >
            <img
              src={img.url}
              alt={`图片 ${idx + 1}`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <View style={styles.legacyCardContainer}>
      {noteText ? (
        <Text style={{ fontSize: 14, color: '#374151', lineHeight: 20, marginBottom: (normalizedLink || galleryImages.length > 0) ? 12 : 0 }}>
          {noteText}
        </Text>
      ) : null}

      {isExternalLike && basePayloadImage ? (
        <div style={{ width: '100%', height: 160, position: 'relative', overflow: 'hidden', background: '#F3F4F6', marginTop: 8, marginBottom: 8 }}>
          <img
            src={basePayloadImage}
            alt={noteText || '外部預覽圖'}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            loading="lazy"
            onClick={(e) => { e.stopPropagation(); handleOpenLink(); }}
          />
        </div>
      ) : (
        renderImageGallery()
      )}

      {normalizedLink ? (
        <TouchableOpacity
          onPress={handleOpenLink}
          activeOpacity={0.85}
          style={{
            paddingVertical: 14,
            paddingHorizontal: 16,
            borderRadius: 12,
            backgroundColor: '#EEF2FF',
            borderWidth: 1,
            borderColor: 'rgba(79, 70, 229, 0.15)',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <View style={{ flex: 1, marginRight: 12 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <WebIcon name={mapModeMeta?.icon || 'near_me'} size={18} color="#2563EB" family="MaterialIcons" />
              <Text style={{ marginLeft: 10, fontSize: 14, color: '#1D4ED8', fontWeight: '600' }}>
                Google 地圖路線
              </Text>
            </View>
            {(mapModeMeta || mapDuration) ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                {mapModeMeta ? (
                  <Text style={{ fontSize: 12, color: '#4B5563', marginRight: mapDuration ? 6 : 0 }}>
                    {mapModeMeta.shortLabel || mapModeMeta.label}
                  </Text>
                ) : null}
                {mapModeMeta && mapDuration ? (
                  <Text style={{ fontSize: 12, color: '#9CA3AF', marginRight: 6 }}>·</Text>
                ) : null}
                {mapDuration ? (
                  <Text style={{ fontSize: 12, color: '#4B5563' }}>{mapDuration}</Text>
                ) : null}
              </View>
            ) : null}
          </View>
          <WebIcon name="google-maps" size={20} color="#2563EB" />
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

export default NoteAdapter;
